import type { IntercomStatus } from "@/stores/use-intercom-store"

export const SHOW_TIMEOUT_MS = 8000

export type LauncherPhase = "idle" | "queued" | "pending" | "visible"

export type LauncherEvent =
  | { type: "click"; status: IntercomStatus }
  | { type: "dismiss" }
  | { type: "statusChange"; status: IntercomStatus }
  | { type: "onShow" }
  | { type: "onHide" }
  | { type: "showTimeout" }

export type LauncherEffect = "show" | "hide" | "openDocs" | "queuedFailed" | "showConfirmed" | "showTimedOut" | null

export type LauncherResult = { phase: LauncherPhase; effect: LauncherEffect }

const stay = (phase: LauncherPhase): LauncherResult => ({ phase, effect: null })

export function reduce(phase: LauncherPhase, event: LauncherEvent): LauncherResult {
  switch (event.type) {
    case "click":
      if (event.status === "error") return { phase, effect: "openDocs" }
      if (event.status !== "ready") return { phase: "queued", effect: null }
      if (phase === "visible") return { phase: "idle", effect: "hide" }
      if (phase === "queued" || phase === "pending") return stay(phase)
      return { phase: "pending", effect: "show" }
    case "dismiss":
      return phase === "visible" ? { phase: "idle", effect: "hide" } : stay(phase)
    case "statusChange":
      if (phase !== "queued") return stay(phase)
      if (event.status === "ready") return { phase: "pending", effect: "show" }
      if (event.status === "error") return { phase: "idle", effect: "queuedFailed" }
      return stay(phase)
    case "onShow":
      return { phase: "visible", effect: phase === "pending" ? "showConfirmed" : null }
    case "onHide":
      return phase === "queued" || phase === "pending" ? stay(phase) : { phase: "idle", effect: null }
    case "showTimeout":
      return phase === "pending" ? { phase: "idle", effect: "showTimedOut" } : stay(phase)
  }
}

export const isWaiting = (phase: LauncherPhase): boolean => phase === "queued" || phase === "pending"
